import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { setIsOpened } from "../../store/store";
import Icon from "../AppIcon";
import rocketLogo from "../../assets/rocket-logo.png";

const SideNavigation = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, isGlobalAdmin, isTenantAdmin, logout } = useAuth();

  // Send sidebar expanded state to Redux store
  useEffect(() => {
    dispatch(setIsOpened(!isCollapsed));
  }, [isCollapsed, dispatch]);

  const {
    currentLocation,
    locations,
    hasMultipleLocations,
    loading: locationsLoading,
    setCurrentLocation,
  } = useLocationContext();
  const { selectedTenant, tenants, hasMultipleTenants } = useTenantContext();
  const { canAccess, isFreeTier, isTrial, trialDaysRemaining } = useSubscription();
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);

  // Animation state for settings panel
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [settingsAnimating, setSettingsAnimating] = useState(false);
  const settingsTimerRef = useRef(null);

  // Refs for hover debouncing to prevent jitter
  const hoverTimerRef = useRef(null);

  // Role checks
  const isRegularEmployee = user?.role === "User";
  const isManager = user?.role === "Manager";
  const isTroncMaster = user?.role === "TroncMaster";

  // Show location selector if user has locations to choose from
  const showLocationSelector =
    !locationsLoading &&
    locations &&
    locations.length > 0 &&
    !isRegularEmployee;

  // Calculate if sidebar should appear expanded (either permanently or on hover)
  const isVisuallyExpanded = !isCollapsed || isHoverExpanded;

  // =========================================================================
  // RBAC NAVIGATION CONFIGURATION
  // ============================================================================

  // Employee-only navigation (for regular User role)
  const employeeNavItems = [
    {
      path: "/my-tronc",
      label: "My TRONC",
      icon: "PieChart",
    },
    {
      path: "/my-clock-records",
      label: "My Clock Records",
      icon: "Clock",
    },
  ];

  // Build main nav items based on role
  const getMainNavItems = () => {
    if (isRegularEmployee) return [];

    // Manager: Dashboard, Takings, Employees
    if (isManager) {
      return [
        {
          path: "/dashboard-overview",
          label: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          path: "/daily-takings-and-tronc",
          label: "Takings",
          icon: "PoundSterling",
        },
        {
          path: "/employees",
          label: "Employees",
          icon: "Users",
        },
        {
          path: "/clock-records",
          label: "Clock Records",
          icon: "Clock",
        },
      ];
    }

    // TroncMaster: Dashboard, Takings, Employees, Lab, Policy Generator, Reports, Transactions, Clock Records, Audit Trail
    if (isTroncMaster) {
      return [
        {
          path: "/dashboard-overview",
          label: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          path: "/daily-takings-and-tronc",
          label: "Takings",
          icon: "PoundSterling",
        },
        {
          path: "/employees",
          label: "Employees",
          icon: "Users",
        },
        {
          path: "/clock-records",
          label: "Clock Records",
          icon: "Clock",
        },
        {
          path: "/distribution-lab",
          label: "Lab",
          icon: "FlaskConical",
          feature: "distributionLab",
        },
        {
          path: "/policy-generator",
          label: "Policy Generator",
          icon: "FileCode",
          feature: "policyGenerator",
        },
        {
          path: "/reports",
          label: "Reports",
          icon: "BarChart3",
        },
        {
          path: "/transactions",
          label: "Transactions",
          icon: "Receipt",
        },
        {
          path: "/audit-trail",
          label: "Audit Trail",
          icon: "FileText",
        },
      ].filter((item) => !item.feature || canAccess(item.feature));
    }

    // TenantAdmin & GlobalAdmin: Full navigation
    const items = [
      {
        path: "/dashboard-overview",
        label: "Dashboard",
        icon: "LayoutDashboard",
      },
      {
        path: "/daily-takings-and-tronc",
        label: "Takings",
        icon: "PoundSterling",
      },
      {
        path: "/employees",
        label: "Employees",
        icon: "Users",
      },
      {
        path: "/clock-records",
        label: "Clock Records",
        icon: "Clock",
      },
      {
        path: "/distribution-lab",
        label: "Lab",
        icon: "FlaskConical",
        feature: "distributionLab",
      },
      {
        path: "/policy-generator",
        label: "Policy Generator",
        icon: "FileCode",
        feature: "policyGenerator",
      },
      {
        path: "/reports",
        label: "Reports",
        icon: "BarChart3",
      },
      {
        path: "/transactions",
        label: "Transactions",
        icon: "Receipt",
      },
      {
        path: "/audit-trail",
        label: "Audit Trail",
        icon: "FileText",
      },
    ].filter((item) => !item.feature || canAccess(item.feature));

    return items;
  };

  const mainNavItems = getMainNavItems();

  // Highlighted item - Distribution (separate from main nav for visual emphasis)
  // Managers and TenantAdmins can access distribution; GlobalAdmins can view but not act
  const showDistribution = !isRegularEmployee;
  const highlightedItem = {
    path: "/tronc-distribution",
    label: "Distribution",
    icon: "PieChart",
  };

  // Settings groups organized by category - RBAC filtered
  const settingsGroups = [
    {
      label: "Personal",
      items: [
        {
          path: "/settings/profile",
          label: "Profile",
          icon: "User",
          roles: [
            "User",
            "Manager",
            "TroncMaster",
            "TenantAdmin",
            "GlobalAdmin",
          ],
        },
      ],
    },
    {
      label: "Organization",
      items: [
        {
          path: "/settings/company",
          label: "Company Details",
          icon: "Building2",
          roles: ["TroncMaster", "TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/settings/locations",
          label: "Locations",
          icon: "MapPin",
          roles: ["TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/system/departments",
          label: "Departments",
          icon: "LayoutGrid",
          roles: ["TroncMaster", "TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/system/job-roles",
          label: "Job Roles",
          icon: "Briefcase",
          roles: ["Manager", "TroncMaster", "TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/settings/users",
          label: "Users",
          icon: "Users",
          roles: ["TenantAdmin", "GlobalAdmin"],
        },
      ],
    },
    {
      label: "Rules",
      items: [
        {
          path: "/system/eligibility",
          label: "Eligibility Rules",
          icon: "CheckCircle",
          roles: ["TroncMaster", "TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/settings/distribution-rules",
          label: "Distribution Rules",
          icon: "Settings2",
          roles: ["TroncMaster", "TenantAdmin", "GlobalAdmin"],
        },
      ],
    },
    {
      label: "Company Settings",
      items: [
        {
          path: "/settings/subscription",
          label: "Subscription",
          icon: "CreditCard",
          roles: ["TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/settings/api-keys",
          label: "API Keys",
          icon: "KeyRound",
          roles: ["TenantAdmin", "GlobalAdmin"],
        },
        {
          path: "/settings/sync-logs",
          label: "Sync",
          icon: "RefreshCw",
          roles: ["Manager", "TroncMaster", "TenantAdmin", "GlobalAdmin"],
          feature: "sync",
        },
      ],
    },
    {
      label: "System",
      items: [
        {
          path: "/settings/tenants",
          label: "Tenants",
          icon: "Building",
          roles: ["GlobalAdmin"],
        },
        {
          path: "/system/integrations",
          label: "Integration Providers",
          icon: "Plug",
          roles: ["GlobalAdmin"],
        },
        {
          path: "/system/error-logs",
          label: "Error Logs",
          icon: "AlertTriangle",
          roles: ["GlobalAdmin"],
        },
        {
          path: "/system/scheduled-emails",
          label: "Scheduled Emails",
          icon: "Mail",
          roles: ["GlobalAdmin"],
        },
        {
          path: "/system/demo-clear",
          label: "Clear Demo Data",
          icon: "Trash2",
          roles: ["TenantAdmin", "GlobalAdmin"],
        },
      ],
    },
  ];

  // Filter settings groups and items based on user role and subscription features
  const visibleSettingsGroups = settingsGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.roles.includes(user?.role) &&
          (!item.feature || canAccess(item.feature)),
      ),
    }))
    .filter((group) => group.items.length > 0);

  // Get the Company Settings group label with tenant name for global admins
  const getCompanySettingsLabel = () => {
    if (isGlobalAdmin && selectedTenant) {
      return `${selectedTenant.name} Settings`;
    }
    return "Company Settings";
  };

  // Update the label dynamically for the Company Settings group
  const updatedSettingsGroups = visibleSettingsGroups.map((group) => {
    if (group.label === "Company Settings") {
      return {
        ...group,
        label: getCompanySettingsLabel(),
      };
    }
    return group;
  });

  // Check if current path is in settings/system
  const isInSettings =
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/system");

  // Handle settings panel open/close with animation
  const openSettings = useCallback(() => {
    if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current);
    setSettingsExpanded(true);
    setSettingsVisible(false);
    // Trigger the enter animation on the next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSettingsVisible(true);
        setSettingsAnimating(true);
        settingsTimerRef.current = setTimeout(
          () => setSettingsAnimating(false),
          300,
        );
      });
    });
  }, []);

  const closeSettings = useCallback(() => {
    if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current);
    setSettingsVisible(false);
    setSettingsAnimating(true);
    settingsTimerRef.current = setTimeout(() => {
      setSettingsExpanded(false);
      setSettingsAnimating(false);
    }, 300);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current);
    };
  }, []);

  // Auto-collapse settings panel when sidebar is collapsed and not hover-expanded
  useEffect(() => {
    if (isCollapsed && !isHoverExpanded) {
      setSettingsExpanded(false);
      setSettingsVisible(false);
      setSettingsAnimating(false);
    }
  }, [isCollapsed, isHoverExpanded]);

  // Handle hover events for collapsed sidebar with debounce to prevent jitter
  const handleMouseEnter = () => {
    if (isCollapsed) {
      // Debounce: only expand after mouse stays for 150ms
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => {
        setIsHoverExpanded(true);
      }, 150);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      setIsHoverExpanded(false);
    }
  };

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const NavItem = ({ item, isNested = false, isHighlighted = false }) => {
    // If item is coming soon, render a disabled-looking link
    if (item.comingSoon) {
      return (
        <div
          title={`${item.label} - Coming Soon`}
          className={`
     flex items-center gap-3 px-3 py-2 rounded-lg transition-all opacity-50 cursor-not-allowed
   ${isNested ? "text-sm" : ""}
     ${!isVisuallyExpanded ? "justify-center" : ""}
    ${isNested ? "text-muted-foreground" : "text-white/50"}
          `}
        >
          <Icon name={item.icon} size={isNested ? 16 : 20} />
          {isVisuallyExpanded && (
            <span className="font-medium flex items-center gap-2">
              {item.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${isNested ? "bg-muted" : "bg-white/10"}`}
              >
                Soon
              </span>
            </span>
          )}
        </div>
      );
    }

    return (
      <NavLink
        to={item.path}
        onClick={() => {
          onClose?.();
          if (
            !item.path.startsWith("/settings") &&
            !item.path.startsWith("/system")
          ) {
            setSettingsExpanded(false);
          }
        }}
        title={!isVisuallyExpanded ? item.label : undefined}
        className={({ isActive }) => `
      flex items-center gap-3 px-3 py-2 rounded-lg transition-all
    ${isNested ? "text-sm" : ""}
      ${!isVisuallyExpanded ? "justify-center" : ""}
          ${
            isHighlighted && !isActive
              ? isNested
                ? "bg-primary/10 text-primary border border-primary/20"
                : "bg-white/10 text-white border border-white/20"
              : ""
          }
          ${
            isActive
              ? isNested
                ? "bg-primary text-primary-foreground"
                : "bg-white/15 text-white"
              : isHighlighted
                ? isNested
                  ? "hover:bg-primary/20"
                  : "hover:bg-white/15"
                : isNested
                  ? "text-foreground hover:bg-card/50"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
          }`}
      >
        <Icon name={item.icon} size={isNested ? 16 : 20} />
        {isVisuallyExpanded && (
          <span className="font-medium">{item.label}</span>
        )}
      </NavLink>
    );
  };

  const sidebarWidth = isVisuallyExpanded ? "w-64" : "w-16";

  // =========================================================================
  // SIMPLIFIED SIDEBAR FOR REGULAR EMPLOYEES (User role)
  // Shows: My TRONC (coming soon), Policy (coming soon), Profile, Sign Out
  // =========================================================================
  if (isRegularEmployee) {
    return (
      <>
        {/* Mobile Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[999] lg:hidden"
            onClick={onClose}
          />
        )}

        {/* Simplified Sidebar for Employees */}
        <aside
          className={`
 fixed top-0 left-0 z-[1000] h-full ${sidebarWidth} bg-[#1a2b4b] border-r border-white/10
      transform transition-all duration-300 ease-in-out
            ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
  ${isHoverExpanded ? "shadow-2xl" : ""}
  `}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="absolute top-0 -right-3 h-full w-3"
            d
            aria-hidden="true"
          />
          <div className="flex flex-col h-full">
            {/* Header with Logo */}
            <div className="p-2 border-b border-white/10">
              <div className="flex items-center justify-center">
                <NavLink
                  to="/my-tronc"
                  className="flex items-center justify-center"
                >
                  <img
                    src={rocketLogo}
                    alt="Rocket"
                    className="h-10 w-auto object-contain brightness-0 invert"
                  />
                </NavLink>

                {/* Mobile close button */}
                {isVisuallyExpanded && (
                  <button
                    className="lg:hidden p-2 hover:bg-white/10 rounded-md absolute right-3 top-2 text-white"
                    onClick={onClose}
                  >
                    <Icon name="X" size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* User Info */}
            {isVisuallyExpanded ? (
              <div className="p-3 border-b border-white/10 relative">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {user?.firstName?.[0]}
                      {user?.lastName?.[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm text-white">
                      {user?.fullName}
                    </p>
                    <p className="text-xs text-white/50 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-2 border-b border-white/10 flex justify-center relative">
                <div
                  className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"
                  title={user?.fullName}
                >
                  <span className="text-white font-semibold text-sm">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
              </div>
            )}

            {/* Employee Navigation */}
            <nav
              className={`flex-1 ${!isVisuallyExpanded ? "p-2" : "p-3"} space-y-0.5 overflow-y-auto`}
            >
              {isVisuallyExpanded && (
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-2 px-1">
                  Menu
                </p>
              )}
              {employeeNavItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}

              {/* Policy Page */}
              <NavItem
                item={{
                  path: "/policy",
                  label: "Policy",
                  icon: "FileCode",
                }}
              />
            </nav>

            {/* Settings & Sign Out Row */}
            <div
              className={`border-t border-white/10 ${!isVisuallyExpanded ? "p-2" : "p-3"}`}
            >
              <div
                className={`flex gap-2 ${!isVisuallyExpanded ? "flex-col" : ""}`}
              >
                {!isVisuallyExpanded ? (
                  <>
                    <NavLink
                      to="/settings/profile"
                      title="Profile"
                      className={({ isActive }) => `
     flex items-center justify-center px-3 py-2 rounded-lg transition-all
     ${isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}
 `}
                    >
                      <Icon name="User" size={20} />
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      title="Sign Out"
                      className="flex items-center justify-center px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <Icon name="LogOut" size={20} />
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink
                      to="/settings/profile"
                      className={({ isActive }) => `
   flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all text-sm
        ${isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}
          `}
                    >
                      <Icon name="User" size={18} />
                      <span className="font-medium">Profile</span>
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      title="Sign Out"
                      className="flex items-center justify-center px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <Icon name="LogOut" size={18} />
                    </button>
                  </>
                )}
              </div>

              {isVisuallyExpanded && (
                <p className="text-xs text-center text-white/30 mt-2">
                  © {new Date().getFullYear()} Rocket
                </p>
              )}
            </div>
          </div>
        </aside>
      </>
    );
  }

  // =========================================================================
  // FULL SIDEBAR FOR MANAGERS, TENANT ADMINS, AND GLOBAL ADMINS
  // =========================================================================
  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[999] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
  fixed top-0 left-0 z-[1000] h-full ${sidebarWidth} bg-[#1a2b4b] border-r border-white/10
   transform transition-all duration-300 ease-in-out
    ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
${isHoverExpanded ? "shadow-2xl" : ""}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="absolute top-0 -right-3 h-full w-3"
          aria-hidden="true"
        />
        <div className="flex flex-col h-full">
          {/* Header with Logo - Compact */}
          <div className="p-2 border-b border-white/10">
            <div className="flex items-center justify-center">
              <NavLink
                to="/dashboard-overview"
                className="flex items-center justify-center"
              >
                <img
                  src={rocketLogo}
                  alt="Rocket"
                  className={`${isHoverExpanded ? "h-10 w-auto" : "h-8 w-auto"} transition-all duration-300 object-contain brightness-0 invert`}
                />
              </NavLink>

              {/* Mobile close button */}
              {isVisuallyExpanded && (
                <button
                  className="lg:hidden p-2 hover:bg-white/10 rounded-md absolute right-3 top-2 text-white"
                  onClick={onClose}
                >
                  <Icon name="X" size={20} />
                </button>
              )}
            </div>
          </div>

          {/* User Info with Collapse Button - Compact */}
          {isVisuallyExpanded ? (
            <div className="p-3 border-b border-white/10 relative">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <span className="text-white font-semibold text-sm">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate text-sm text-white">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-white/50 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* Collapse "Ear" Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse();
                }}
                onMouseEnter={(e) => e.stopPropagation()}
                className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-10 bg-gray-400 border border-gray-500 rounded-r-md items-center justify-center hover:bg-gray-500 transition-colors z-10 shadow-sm"
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <Icon
                  name="ChevronsLeft"
                  size={14}
                  className={`text-white transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
                />
              </button>

              {/* Role Badge */}
              <div className="mt-2">
                <span
                  className={`badge text-xs ${
                    user?.role === "GlobalAdmin"
                      ? "bg-[#FF5A5F]/20 text-[#FF5A5F] border-[#FF5A5F]/30"
                      : user?.role === "TenantAdmin"
                        ? "bg-[#00C9A7]/20 text-[#00C9A7] border-[#00C9A7]/30"
                        : user?.role === "TroncMaster"
                          ? "bg-purple-400/20 text-purple-300 border-purple-400/30"
                          : user?.role === "Manager"
                            ? "bg-amber-400/20 text-amber-300 border-amber-400/30"
                            : "bg-white/10 text-white/60 border-white/20"
                  }`}
                >
                  {user?.role?.replace(/([A-Z])/g, " $1").trim()}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-2 border-b border-white/10 flex flex-col items-center gap-2 relative">
              <div
                className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"
                title={`${user?.fullName} - ${selectedTenant?.name || "No tenant"} - ${currentLocation?.name || "All locations"}`}
              >
                <span className="text-white font-semibold text-sm">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              </div>
              {/* Collapse "Ear" Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse();
                }}
                onMouseEnter={(e) => e.stopPropagation()}
                className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-10 bg-gray-400 border border-gray-500 rounded-r-md items-center justify-center hover:bg-gray-500 transition-colors z-10 shadow-sm"
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <Icon
                  name="ChevronsLeft"
                  size={14}
                  className={`text-white transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          )}

          {/* Settings Expanded View - Takes over entire sidebar */}
          {settingsExpanded && isVisuallyExpanded ? (
            <div
              className={`flex-1 flex flex-col bg-white overflow-hidden transition-all duration-300 ease-in-out ${
                settingsVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-4"
              }`}
            >
              {/* Settings Header with Back Button - Compact */}
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <button
                  onClick={closeSettings}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white transition-all text-gray-700"
                >
                  <Icon
                    name="ChevronLeft"
                    size={18}
                    className={`transition-transform duration-300 ${settingsVisible ? "translate-x-0" : "-translate-x-2"}`}
                  />
                  <span className="font-semibold text-sm">Back</span>
                </button>
              </div>

              {/* Settings Navigation Groups */}
              <nav className="flex-1 overflow-y-auto">
                {updatedSettingsGroups.map((group, groupIndex) => (
                  <div
                    key={group.label}
                    className={`transition-all duration-300 ease-in-out ${
                      settingsVisible
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 -translate-x-2"
                    }`}
                    style={{
                      transitionDelay: settingsVisible
                        ? `${(groupIndex + 1) * 50}ms`
                        : "0ms",
                    }}
                  >
                    <div className="px-4 py-2 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        {group.label}
                      </p>
                    </div>
                    <div className="px-2 pb-2 space-y-1 bg-white">
                      {group.items.map((item) => (
                        <NavItem key={item.path} item={item} isNested />
                      ))}
                    </div>
                  </div>
                ))}
              </nav>

              {/* Sign Out in Settings Mode - Compact */}
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-700 hover:bg-white transition-all text-sm"
                >
                  <Icon name="LogOut" size={18} />
                  <span className="font-medium">Sign Out</span>
                </button>
                <p className="text-xs text-center text-gray-400 mt-2">
                  © {new Date().getFullYear()} Rocket
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Highlighted Item - Distribution (at top of nav) */}
              {showDistribution && (
                <div
                  className={`${!isVisuallyExpanded ? "p-2" : "px-3 pt-3 pb-1"}`}
                >
                  {isVisuallyExpanded && (
                    <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-2 px-1">
                      Quick Action
                    </p>
                  )}
                  <NavItem item={highlightedItem} isHighlighted />
                </div>
              )}

              {/* Main Navigation - Compact */}
              <nav
                className={`flex-1 ${!isVisuallyExpanded ? "p-2" : "p-3"} space-y-0.5 overflow-y-auto`}
              >
                {isVisuallyExpanded && (
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-2 px-1">
                    Menu
                  </p>
                )}
                {mainNavItems.map((item) => (
                  <NavItem key={item.path} item={item} />
                ))}
              </nav>

              {/* Settings & Sign Out Row - Combined at bottom - Compact */}
              <div
                className={`border-t border-white/10 ${!isVisuallyExpanded ? "p-2" : "p-3"}`}
              >
                {visibleSettingsGroups.length > 0 && (
                  <div
                    className={`flex gap-2 ${!isVisuallyExpanded ? "flex-col" : ""}`}
                  >
                    {/* Settings Button */}
                    {!isVisuallyExpanded ? (
                      <>
                        <NavLink
                          to="/settings/profile"
                          title="Settings"
                          className={({ isActive }) => `
         flex items-center justify-center px-3 py-2 rounded-lg transition-all
         ${
           isInSettings
             ? "bg-white/15 text-white"
             : "text-white/70 hover:bg-white/10 hover:text-white"
         }
      `}
                        >
                          <Icon name="Settings" size={20} />
                        </NavLink>
                        <button
                          onClick={handleLogout}
                          title="Sign Out"
                          className="flex items-center justify-center px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
                        >
                          <Icon name="LogOut" size={20} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            settingsExpanded ? closeSettings() : openSettings()
                          }
                          className={`
     flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all text-sm
    ${isInSettings ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}
        `}
                        >
                          <Icon
                            name="Settings"
                            size={18}
                            className={`transition-transform duration-500 ${settingsExpanded ? "rotate-90" : ""}`}
                          />
                          <span className="font-medium">Settings</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          title="Sign Out"
                          className="flex items-center justify-center px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
                        >
                          <Icon name="LogOut" size={18} />
                        </button>
                      </>
                    )}
                  </div>
                )}

                {isVisuallyExpanded && (
                  <p className="text-xs text-center text-white/30 mt-2">
                    © {new Date().getFullYear()} Rocket
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default SideNavigation;
